import React from "react";
import { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import AnalysisResults, { ResultLevel } from "@/components/AnalysisResults";
import { AnalysisResult } from "@/hooks/useContentAnalysis";
import AnalysisProgress from "./AnalysisProgress";

type ResultsAreaProps = {
  results: AnalysisResult | null;
  icon: LucideIcon;
  emptyTitle?: string;
  emptyDescription?: string;
  tipTitle?: string;
  tipDescription?: string;
  onExport?: () => void;
  onNewAnalysis: () => void;
  partialResults?: Partial<AnalysisResult> & { progress?: number };
  analysisStatus?: 'idle' | 'processing' | 'completed' | 'error';
  isLoading?: boolean;
  progress?: number;
};

const ResultsArea = ({
  results,
  partialResults,
  analysisStatus = 'idle',
  isLoading = false,
  progress = 0,
  icon: Icon,
  emptyTitle = "No Analysis Results Yet",
  emptyDescription = "Upload or paste content to begin analysis. Results will appear here once processing is complete.",
  tipTitle = "Tip",
  tipDescription = "For best results, provide longer text samples with at least several paragraphs.",
  onExport,
  onNewAnalysis
}: ResultsAreaProps) => {
  
  // Determine what to display based on analysis status and results
  const showPartialResults = !results && partialResults?.results && partialResults.results.length > 0;
  const showEmptyState = !results && !showPartialResults && !isLoading;
  const isProcessing = analysisStatus === 'processing' || isLoading;
  const hasResults = results !== null && !isProcessing;
  
  // Calculate actual progress percentage for the overlay
  // Ensure progress is always between 10-90% during processing to avoid UI jumps
  const displayProgress = isProcessing ? 
    Math.min(90, Math.max(10, progress || partialResults?.progress || 35)) : 
    (analysisStatus === 'completed' ? 100 : 0);

  // Handle button clicks
  const handleExport = () => {
    if (onExport && hasResults) {
      onExport();
    }
  };

  const handleNewAnalysis = () => {
    if (!isProcessing) {
      onNewAnalysis();
    }
  };
  
  return (
    <>
      {results ? (
        // Show complete results
        <Card className="relative">
          <CardContent className="pt-6">
            <AnalysisResults {...results} isLoading={isLoading} />
          </CardContent>
        </Card>
      ) : showPartialResults ? (
        // Show partial results with processing indicator
        <Card className="relative">
          <div className={`absolute inset-0 bg-background/80 backdrop-blur-[1px] z-10 flex items-center justify-center ${!isProcessing ? 'hidden' : ''}`}>
            <div className="w-4/5 max-w-md p-4">
              <AnalysisProgress 
                progress={displayProgress}
                isAnalyzing={isProcessing}
                status={analysisStatus} 
              />
            </div>
          </div>
          <CardContent className="pt-6">
            {partialResults?.results && (
              <AnalysisResults 
                results={partialResults.results} 
                overallStatus={partialResults.overallStatus || "suspicious"}
                isLoading={isLoading}
              />
            )}
          </CardContent>
        </Card>
      ) : (
        // Show empty state or loading state
        <Card>
          <CardContent className="pt-6">
            {isProcessing ? (
              <div className="flex flex-col items-center justify-center py-12">
                <AnalysisProgress 
                  progress={displayProgress}
                  isAnalyzing={true}
                  status="processing"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Icon className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">{emptyTitle}</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-md">
                  {emptyDescription}
                </p>
                <Alert variant="default" className="max-w-md border-fraudeye-accent/50 bg-fraudeye-accent/10">
                  <Icon className="h-4 w-4 text-fraudeye-accent" />
                  <AlertTitle>{tipTitle}</AlertTitle>
                  <AlertDescription>
                    {tipDescription}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        {onExport && (
          <Button 
            className="w-full" 
            variant="outline"
            onClick={handleExport}
            disabled={!hasResults}
          >
            {isProcessing ? 'Please wait...' : 'Export Results'}
          </Button>
        )}
        <Button 
          className={`w-full bg-fraudeye-accent hover:bg-fraudeye-highlight ${!onExport ? 'col-span-2' : ''}`}
          onClick={handleNewAnalysis}
          disabled={isProcessing}
        >
          {isProcessing ? 'Analysis in Progress...' : 'New Analysis'}
        </Button>
      </div>
    </>
  );
};

export default ResultsArea;
